import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import AlertHelper from 'src/presentation/helpers/AlertHelper';
import Translations from 'src/core/utils/Translations';
import Constants from 'src/core/utils/Constants';
import errorHandler from 'src/core/utils/errorHandler';
import { extractEventCode } from 'src/core/utils/extractEventCode';
import { Platform } from 'react-native';

/** Een al toegevoegd event opzoeken op code of uuid (de deeplink kan beide bevatten). */
function findAddedEvent(identifier: string) {
	// Event codes are case-insensitive (the backend matches them via a case-insensitive DB collation), so
	// compare case-insensitively; otherwise a deeplink like `topev://event/topitest` would not match an
	// already-added event whose stored code is "TopiTest".
	const needle = identifier.toLowerCase();
	return useEventStore
		.getState()
		.eventData.find((event) => event.code.toLowerCase() === needle || event.uuid.toLowerCase() === needle);
}

/** Terug naar de tabs (Index), bv. bij annuleren of een mislukte toevoeging. */
function goToTabs(): void {
	useNavigationStore.getState().resetToRoutes([{ name: Constants.screenIndex }]);
}

/**
 * Maakt een event het actieve event: thema, pagina's, en het uuid dat elk scherm leest.
 *
 * Split out of openEventDetail because a deep web URL like
 * `/evenement/<code>/programma` needs the event active WITHOUT being sent to the
 * event's own home screen.
 */
async function activateEvent(uuid: string): Promise<void> {
	const eventStore = useEventStore.getState();
	eventStore.updateTheme(uuid);
	// Load the event's pages (cache-first) before showing anything, same as a normal tile tap.
	// Without this a screen renders "Geen inhoud" because the pages are not in state yet.
	await eventStore.fetchEvent(uuid);
	useNavigationStore.getState().setActiveEventByUuid(uuid);
}

/**
 * Activeert het event dat een diepe URL noemt, en laat de bezoeker staan op het
 * scherm waar de URL om vroeg.
 *
 * Alleen web komt hier: in de app heb je het event al gekozen voordat je op een van
 * zijn pagina's kunt staan. Een event dat niet in de lokale lijst staat heeft geen
 * pagina's om te tonen, dus dat valt terug op de QR-beslisboom, die aanbiedt het toe
 * te voegen en het daarna opent.
 */
export async function activateEventFromPath(code: string): Promise<boolean> {
	// The stored events have to be in state before we can tell whether this is one of them. On a
	// cold browser load they are not: init() only runs from HomeScreen and EventDeeplinkScreen, both
	// of them after NavigationContainer's onReady. Without this await, a visitor opening a shared
	// link to an event they already have was asked whether to ADD it, and cancelling threw them off
	// the very screen the link pointed at.
	if (useEventStore.getState().eventData.length === 0) {
		await useEventStore.getState().init();
	}

	const existing = findAddedEvent(code);
	if (!existing) {
		handleEventDeeplink(code);

		return false;
	}

	if (useEventStore.getState().activeEventUuid === existing.uuid) return true;

	await activateEvent(existing.uuid);

	return true;
}

/**
 * Opent het detailscherm van een reeds toegevoegd event. Vervangt de hele stack door
 * [Index, Event], zodat het event over de tabs opent en terug-swipen naar het overzicht gaat.
 */
async function openEventDetail(uuid: string, title: string, logo: string | null): Promise<void> {
	await activateEvent(uuid);
	useNavigationStore.getState().resetToRoutes([
		{ name: Constants.screenIndex },
		// eventUuid erbij, anders kan er op web geen URL voor dit scherm bestaan: de serialisatie
		// leidt de publieke code daar uit af.
		{ name: Constants.screenEvent, params: { title, eventLogo: logo, eventUuid: uuid } },
	]);
}

async function addAndOpen(code: string): Promise<void> {
	await useEventStore.getState().addEvent(code);
	const added = findAddedEvent(code);
	if (added) {
		if (returnToRequestedPath()) return;

		await openEventDetail(added.uuid, added.name, added.logo ?? null);
	} else {
		// Add failed (invalid code, or addEvent already showed its own message): leave the resolver screen and
		// return to the tabs so the user is not stuck on the loader.
		goToTabs();
	}
}

/**
 * On web the shared link is the normal way in, and it usually points deeper than the event's home:
 * a programme, a news item. The decision tree that adds the event ends on the event home, so the
 * visitor lands somewhere other than where the link pointed -- the one thing a shared link has to
 * get right.
 *
 * Now that the event is in the list, the address bar already holds the right URL and resolving it
 * is exactly what a cold load does. Reloading is what makes the app read it again, and it costs a
 * cached bundle rather than a second resolver path to maintain alongside the first.
 *
 * Returns whether it took over. Native never gets here (no address bar), and neither does a link
 * that points at the event home, where the normal flow already ends up.
 */
function returnToRequestedPath(): boolean {
	if (Platform.OS !== 'web' || typeof window === 'undefined') return false;

	const path = window.location.pathname ?? '';
	// Two segments is /evenement/<code>: the event home, which openEventDetail already opens.
	if (path.split('/').filter(Boolean).length <= 2) return false;

	window.location.reload();

	return true;
}

/**
 * Already added -> open detail (no confirmation).
 */
export function resolveAddedOrConfirm(code: string): void {
	const existing = findAddedEvent(code);
	if (existing) {
		openEventDetail(existing.uuid, existing.name, existing.logo ?? null).catch(() => undefined);
		return;
	}

	// Not in the local list.
	if (!useNavigationStore.getState().isConnected) {
		errorHandler(Translations.errorDefaultSubject, Translations.errorAddEventOffline);
		goToTabs();
		return;
	}

	// Validate the code first (read-only, no server-side attach).
	useEventStore
		.getState()
		.eventExists(code)
		.then((exists) => {
			if (!exists) {
				errorHandler(Translations.errorDefaultSubject, Translations.errorAddEvent);
				goToTabs();
				return;
			}
			AlertHelper.showConfirmAlert({
				title: Translations.alertAddEventTitle,
				message: Translations.alertAddEventConfirmMessage,
				confirmText: Translations.buttonAdd,
				cancelText: Translations.buttonCancel,
				onConfirm: () => {
					addAndOpen(code).catch(() => undefined);
				},
				onCancel: goToTabs,
			});
		})
		.catch(() => {
			errorHandler(Translations.errorDefaultSubject, Translations.errorAddEventFailed);
			goToTabs();
		});
}

/**
 * Centrale afhandeling van een event-deeplink/QR.
 */
export function handleEventDeeplink(raw: string): void {
	const code = extractEventCode(raw);
	if (!code) {
		errorHandler(Translations.errorDefaultSubject, Translations.errorAddEvent);
		return;
	}

	if (useAuthenticationStore.getState().isLoggedin !== true) {
		useEventStore.getState().setPendingEventCode(code);
		AlertHelper.showConfirmAlert({
			title: Translations.alertLoginRequiredTitle,
			message: Translations.alertLoginRequiredMessage,
			confirmText: Translations.buttonLogin,
			cancelText: Translations.buttonCancel,
			onConfirm: () =>
				useNavigationStore.getState().resetToRoutes([{ name: Constants.screenIndex }, { name: Constants.screenLogin }]),
			onCancel: () => {
				// Drop the remembered code so a later, unrelated login does not resume this deeplink.
				useEventStore.getState().setPendingEventCode(null);
				goToTabs();
			},
		});
		return;
	}

	resolveAddedOrConfirm(code);
}
